import { Box } from '@mui/material';
import { BoxArrowUpRight } from '@styled-icons/bootstrap/BoxArrowUpRight';
import DocumentSkeleton from 'FeatureModules/Document/Components/DocumentSkeleton';
import DocumentTable from 'FeatureModules/Document/Components/Tables/DocumentTable';
import { IDocumentTableBodyAction } from 'FeatureModules/Document/Components/Tables/DocumentTableBody';
import { EDocumentsTableColumnKeys } from 'FeatureModules/Document/utils/documentHeaderColumns';
import Filter from 'Components/Filter/Filter';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import { ThemeButton } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { IBaseDocument } from 'FeatureModules/Document/types/document.types';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';
import ActionContainer from 'Components/ActionContainer';
import useSharedDocuments from '../hooks/useSharedDocument';

const SharedDocuments: FC = () => {
  const {
    error,
    loading,
    documents,
    searchTerm,
    hasNextPage,
    filters,
    handleFilterChange,
    handleFilterReset,
    handleSearchTermChange,
    handlePageEndReached,
    handleNewDocumentRequestClick,
  } = useSharedDocuments();
  const navigate = useNavigate();

  const documentTableActions: IDocumentTableBodyAction[] = [
    {
      icon: <BoxArrowUpRight size="15" />,
      tooltip: 'Open in new tab',
      onClick: (document: IBaseDocument) => window.open(document.url, '_blank'),
      dataTestId: 'download-document-btn',
    },
  ];

  const opeDocumentOverview = (document: IBaseDocument) => {
    navigate(`/documents/shared/${document.id}`);
  };

  if (error) {
    return <ErrorState />;
  }

  if (!documents.length && !searchTerm && !filters.some(({ value }) => !!value) && !loading) {
    return (
      <Box mt={4}>
        <EmptySearchResults skeletonComponent={<DocumentSkeleton />} />
      </Box>
    );
  }

  return (
    <>
      <Box position="relative">
        <Filter
          filters={filters}
          onFilterChange={handleFilterChange}
          onFilterReset={handleFilterReset}
        />

        <ActionContainer gap={2}>
          <SearchToolbar
            setSearchTerm={handleSearchTermChange}
            searchTerm={searchTerm}
            data-testid="document-search-input"
            placeholder="Search document"
            className="search-input"
          />
          <ThemeButton
            color="PRIMARY"
            startIcon={<Icon name="plus" color="WHITE" />}
            onClick={handleNewDocumentRequestClick}
          >
            Request new document
          </ThemeButton>
        </ActionContainer>

        {loading && (
          <Box position="relative" minHeight={200}>
            <Loader />
          </Box>
        )}

        {!!documents.length && !loading && (
          <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
            <DocumentTable
              columns={[
                EDocumentsTableColumnKeys.TITLE,
                EDocumentsTableColumnKeys.CREATOR,
                EDocumentsTableColumnKeys.USAGE,
                EDocumentsTableColumnKeys.ISSUE_DATE,
                EDocumentsTableColumnKeys.EXPIRY_DATE,
                EDocumentsTableColumnKeys.ACTIONS,
              ]}
              documents={documents}
              actions={documentTableActions}
              onDocumentClick={opeDocumentOverview}
              disableSelection
            />
          </InfiniteScrollWrapper>
        )}
      </Box>

      {!documents?.length && !loading && (
        <Box position="relative" minHeight={400}>
          <EmptySearchResults
            message="No search results found"
            skeletonComponent={<DocumentSkeleton title={searchTerm} />}
          />
        </Box>
      )}
    </>
  );
};

export default SharedDocuments;
